// Add to Schedule, on the drilling / loops / small-jobs cards (Ryan, Oct 19 2026). Executives
// only. Pick people (search, or tap a frequent name for this workflow), confirm the days and
// the hours, Review shows exactly what changes in QuickBooks Time — someone already on
// another job that day is MOVED here — then Confirm writes it.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../../services/apiClient';
import { workdayEnd } from '../../utils/workdays';

const sessionRole = () => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role || ''; } catch { return ''; } };
export const canAddToSchedule = () => ['Executive', 'admin'].includes(sessionRole());

const todayYmd = () => new Date().toLocaleDateString('en-CA');
const at = s => new Date(`${s}T12:00:00`);
const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayChip = s => at(s).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
// Every weekday from start through the card's length.
function weekdays(start, days) {
  if (!start) return [];
  const end = workdayEnd(start, Math.max(1, Number(days) || 1)) || start;
  const out = [];
  for (const d = at(start); ymd(d) <= end; d.setDate(d.getDate() + 1)) if (d.getDay() !== 0 && d.getDay() !== 6) out.push(ymd(d));
  return out;
}
const MAX_DAYS = 20;   // the server takes up to 20 days in one go
const lbl = { fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 };
const inp = { border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 14, boxSizing: 'border-box', background: '#fff', fontFamily: 'inherit' };
const WF_NAME = { vertical_drilling: 'Vertical Drilling', horizontal_drilling: 'Directional Drilling', loops: 'Loops', small_jobs: 'Small Jobs', job: 'this job' };

export default function AddToSchedule({ projectId, workflow, startDate, days }) {
  const [open, setOpen] = useState(false);
  if (!canAddToSchedule() || !projectId) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #1e3a5f', background: '#eff6ff', color: '#1e3a5f', fontSize: 14, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        🗓️ Add to Schedule
      </button>
      {open && <AddToScheduleWindow projectId={projectId} workflow={workflow} startDate={startDate} days={days} onClose={() => setOpen(false)} />}
    </>
  );
}

// Also opened on its own — the Big Picture calendar's Schedule button (workflow 'job').
export function AddToScheduleWindow({ projectId, workflow, startDate, days, people, onClose, onConfirmed }) {
  const [info, setInfo] = useState(null);       // { jobcodeId, jobLabel, people, frequent }
  const [err, setErr] = useState('');
  // The days, tapped on a calendar (Ryan, Oct 27 2026). It opens with the card's run of
  // weekdays from its start date — or from today, when that date has already gone by.
  const start0 = startDate && startDate >= todayYmd() ? startDate : todayYmd();
  const [sel, setSel] = useState(() => new Set(weekdays(start0, Math.min(MAX_DAYS, Math.max(1, Math.ceil(Number(days) || 1))))));
  const [t1, setT1] = useState('07:00');
  const [t2, setT2] = useState('15:30');
  // Already picked when a supervisor was dragged onto the job from the Big Picture (Ryan, Oct 31 2026).
  const [picked, setPicked] = useState(() => (Array.isArray(people) ? people : []));     // [{ id, name }]
  const [q, setQ] = useState('');
  const [review, setReview] = useState(null);
  // Once Review answers, the window scrolls down to it so Confirm is right there (Ryan, Oct 31 2026).
  const bodyRef = useRef(null);
  useEffect(() => { if (review && bodyRef.current) bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' }); }, [review]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    apiFetch(`/schedule/card-crew?projectId=${encodeURIComponent(projectId)}&workflow=${encodeURIComponent(workflow || '')}`)
      .then(setInfo).catch(e => setErr(e?.error || 'Could not reach QuickBooks Time.'));
  }, [projectId, workflow]);

  const dates = useMemo(() => [...sel].sort(), [sel]);
  const isPicked = id => picked.some(p => String(p.id) === String(id));
  const toggle = p => { setReview(null); setPicked(list => (list.some(x => String(x.id) === String(p.id)) ? list.filter(x => String(x.id) !== String(p.id)) : [...list, { id: String(p.id), name: p.name }])); };
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s || !info) return [];
    return (info.people || []).filter(p => p.name.toLowerCase().includes(s)).slice(0, 12);
  }, [q, info]);

  async function go(confirm) {
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/schedule/card-crew', { method: 'POST', body: JSON.stringify({ projectId, workflow, dates, personIds: picked.map(p => p.id), start: t1, end: t2, confirm }) });
      if (confirm) {
        setDone(r);
        // Someone is on the job those days — the Big Picture puts a job picked from its search on them (Ryan, Oct 31 2026).
        if (onConfirmed && r && r.totals && (r.totals.done || r.totals.already)) onConfirmed(dates);
      } else setReview(r);
    } catch (e) { setErr(e?.error || 'QuickBooks Time did not take it.'); }
    setBusy(false);
  }
  const changed = () => setReview(null);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 620, maxWidth: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 48px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #eef0f2', display: 'flex', alignItems: 'center', gap: 10, background: '#1e3a5f', color: '#fff', borderRadius: '14px 14px 0 0' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>🗓️ Add to Schedule</div>
            <div style={{ fontSize: 12.5, opacity: 0.85, marginTop: 2 }}>{info ? (info.jobLabel || 'Not in QuickBooks Time yet') : 'Reading QuickBooks Time…'}{WF_NAME[workflow] ? ` · ${WF_NAME[workflow]}` : ''}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ width: 34, height: 34, borderRadius: 999, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>

        <div ref={bodyRef} style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {done ? (
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: done.totals.failed ? '#b45309' : '#15803d' }}>
                {done.totals.done ? `✓ ${done.totals.done} shift${done.totals.done === 1 ? '' : 's'} on ${done.jobLabel} in QuickBooks Time.` : 'Nothing needed changing.'}
              </div>
              {done.totals.retime > 0 && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>{done.totals.retime} of those were already on this job — only the hours changed.</div>}
              {done.totals.already > 0 && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>{done.totals.already} were already on this job with these hours.</div>}
              {done.totals.off > 0 && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>🌴 {done.totals.off} day{done.totals.off === 1 ? '' : 's'} skipped — time off is never moved.</div>}
              {done.failed.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#b91c1c' }}>
                  Didn't go: {done.failed.map(f => `${f.person} ${dayChip(f.date)} (${f.error})`).join('; ')}
                </div>
              )}
              <button onClick={onClose} style={{ marginTop: 16, padding: '10px 20px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>Done</button>
            </div>
          ) : (<>
            {info && !info.jobcodeId && <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '9px 11px' }}>This job isn't in QuickBooks Time yet — add it from the job's QuickBooks Time tile (Add to TSheets) first.</div>}

            {/* Days — tapped on the calendar. Once Review answers, the calendar folds to one line so the
                review and Confirm are in view without scrolling (Ryan, Nov 1 2026); Change opens it again. */}
            {review ? (
              <div>
                <label style={lbl}>Days</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13.5 }}>
                  <b>{dates.length} day{dates.length === 1 ? '' : 's'}</b>
                  <span style={{ color: '#374151' }}>{dates.length ? (dates.length === 1 ? dayChip(dates[0]) : `${dayChip(dates[0])} – ${dayChip(dates[dates.length - 1])}`) : ''}</span>
                  <button type="button" onClick={changed} style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Change</button>
                </div>
              </div>
            ) : (
              <DayCalendar sel={sel} first={start0} onChange={n => { setSel(n); changed(); }} />
            )}

            {/* Hours */}
            <div>
              <label style={lbl}>Hours</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="time" value={t1} onChange={e => { setT1(e.target.value); changed(); }} style={inp} />
                <span style={{ color: '#6b7280' }}>to</span>
                <input type="time" value={t2} onChange={e => { setT2(e.target.value); changed(); }} style={inp} />
              </div>
            </div>

            {/* People */}
            <div>
              <label style={lbl}>People</label>
              {!info && <div style={{ fontSize: 12.5, color: '#9ca3af', marginBottom: 8 }}>Reading QuickBooks Time…</div>}
              {/* The field supervisors first; tap one and the people who usually work with them come up under
                  them (Ryan, Nov 1 2026: "lots of people always work together"). After Review the chip lists fold
                  away and only the picked names stay, so the review is in view. */}
              {info && !review && (info.supervisors || []).length > 0 && (
                <>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Field supervisors — tap one, and the people usually with them come up:</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    {info.supervisors.map(p => <Chip key={p.id} p={p} on={isPicked(p.id)} onClick={() => toggle(p)} title={p.trade || 'Field supervisor'} bold />)}
                  </div>
                  {picked.filter(p => ((info.crews || {})[p.id] || []).length > 0).map(sup => (
                    <div key={`crew:${sup.id}`} style={{ marginBottom: 10, paddingLeft: 10, borderLeft: '3px solid #bbf7d0' }}>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Usually with {sup.name} (last 8 weeks):</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {info.crews[sup.id].map(p => <Chip key={p.id} p={p} on={isPicked(p.id)} onClick={() => toggle(p)} title={`${p.days} day${p.days === 1 ? '' : 's'} with ${sup.name}`} />)}
                      </div>
                    </div>
                  ))}
                </>
              )}
              {info && !review && (info.frequent || []).length > 0 && (
                <>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Often on {WF_NAME[workflow] || 'this work'} (last 8 weeks):</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    {info.frequent.map(p => <Chip key={p.id} p={p} on={isPicked(p.id)} onClick={() => toggle(p)} title={`${p.days} day${p.days === 1 ? '' : 's'} on ${WF_NAME[workflow] || 'this work'}`} />)}
                  </div>
                </>
              )}
              {!review && <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search anyone in QuickBooks Time…" style={{ ...inp, width: '100%' }} />}
              {!review && matches.length > 0 && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                  {matches.map(p => (
                    <button key={p.id} type="button" onClick={() => { toggle(p); setQ(''); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 11px', border: 'none', borderBottom: '1px solid #f3f4f6', background: isPicked(p.id) ? '#f0fdf4' : '#fff', fontSize: 13.5, cursor: 'pointer' }}>
                      {isPicked(p.id) ? '✓ ' : ''}{p.name}
                    </button>
                  ))}
                </div>
              )}
              {picked.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                  {picked.map(p => (
                    <span key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 6px 5px 11px', borderRadius: 999, background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 700 }}>
                      {p.name}<button type="button" onClick={() => toggle(p)} aria-label={`Remove ${p.name}`} style={{ border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: 999, width: 20, height: 20, cursor: 'pointer', fontSize: 12 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Review */}
            {review && (
              <div style={{ border: '1.5px solid #bfdbfe', background: '#f8fafc', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: '#1e3a5f', marginBottom: 8 }}>
                  This will: {[review.totals.move && `move ${review.totals.move} shift${review.totals.move === 1 ? '' : 's'} here`, review.totals.add && `add ${review.totals.add} new shift${review.totals.add === 1 ? '' : 's'}`, review.totals.retime && `change the hours on ${review.totals.retime} shift${review.totals.retime === 1 ? '' : 's'}`].filter(Boolean).join(' and ') || 'change nothing'}{review.totals.already ? ` (${review.totals.already} already on this job with these hours)` : ''}{review.totals.off ? ` · 🌴 ${review.totals.off} on time off — skipped` : ''} — {review.start}–{review.end}
                </div>
                {!(review.totals.move + review.totals.add + (review.totals.retime || 0)) && (
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Everyone picked is already on this job on those days with these hours, so there is nothing to put in QuickBooks Time. Change the days, the hours or the people and Review again — or Cancel.</div>
                )}
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {review.plan.map((x, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, padding: '3px 0', color: x.action === 'already' || x.action === 'off' ? '#9ca3af' : '#111827' }}>
                      <span style={{ width: 96, color: '#6b7280', flexShrink: 0 }}>{dayChip(x.date)}</span>
                      <span style={{ fontWeight: 700, minWidth: 0 }}>{x.person}</span>
                      <span style={{ color: x.action === 'move' || x.action === 'retime' ? '#b45309' : x.action === 'add' ? '#15803d' : '#9ca3af' }}>
                        {x.action === 'move' ? `← moves from ${x.from}${x.others ? ` (+${x.others} more shift${x.others === 1 ? '' : 's'} that day stay)` : ''}` : x.action === 'add' ? 'new shift' : x.action === 'retime' ? `hours change: ${x.was} → ${review.start}–${review.end}` : x.action === 'off' ? `🌴 time off (${x.why}) — skipped` : 'already here'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* The buttons stay at the bottom of the window — no scrolling to Review or Confirm (Ryan, Oct 31 2026). */}
            <div style={{ position: 'sticky', bottom: -18, margin: '0 -18px -18px', padding: '12px 18px', background: '#fff', borderTop: '1px solid #eef0f2', borderRadius: '0 0 14px 14px', zIndex: 1 }}>
            {err && <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700, marginBottom: 8 }}>{err}</div>}
            {/* Why Review is locked, said next to it (Ryan, Nov 1 2026: "the review button is locked") */}
            {!err && !review && !info && <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 8 }}>Reading QuickBooks Time… Review opens as soon as it answers.</div>}
            {!err && !review && info && !info.jobcodeId && <div style={{ fontSize: 12.5, color: '#b45309', fontWeight: 700, marginBottom: 8 }}>Review is locked: this job isn't in QuickBooks Time yet — add it from the job's QuickBooks Time tile (Add to TSheets) first.</div>}
            {!err && !review && info && info.jobcodeId && (!picked.length || !dates.length) && <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 8 }}>{!dates.length ? 'Tap at least one day.' : 'Pick at least one person.'}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} disabled={busy} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13.5, cursor: 'pointer' }}>Cancel</button>
              {!review ? (
                <button onClick={() => go(false)} disabled={busy || !info || !info.jobcodeId || !picked.length || !dates.length}
                  style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: busy || !picked.length || !dates.length ? '#94a3b8' : '#1e3a5f', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' }}>{busy ? 'Checking…' : `Review — ${picked.length} ${picked.length === 1 ? 'person' : 'people'} × ${dates.length} day${dates.length === 1 ? '' : 's'}`}</button>
              ) : !(review.totals.move + review.totals.add + (review.totals.retime || 0)) ? (
                // Nothing to do: say so instead of a green button that doesn't press (Ryan, Nov 1 2026).
                <button disabled title="Everyone picked is already on this job with these hours"
                  style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#e5e7eb', color: '#6b7280', fontSize: 13.5, fontWeight: 800, cursor: 'not-allowed' }}>Nothing to change</button>
              ) : (
                <button onClick={() => go(true)} disabled={busy}
                  style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: busy ? '#86efac' : '#15803d', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' }}>{busy ? 'Saving…' : '✓ Confirm — put it in QuickBooks Time'}</button>
              )}
            </div>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}

// A month calendar: tap a day to add it or take it off; the arrows change the month. Days already
// gone can't be picked; weekends can (they show lighter).
// One name to tap: green with a tick when picked.
function Chip({ p, on, onClick, title, bold = false }) {
  return (
    <button type="button" onClick={onClick} title={title}
      style={{ padding: '6px 11px', borderRadius: 999, border: `1.5px solid ${on ? '#15803d' : bold ? '#1e3a5f' : '#e5e7eb'}`, background: on ? '#f0fdf4' : '#fff', color: '#111827', fontSize: 13, fontWeight: bold ? 800 : 700, cursor: 'pointer' }}>
      {on ? '✓ ' : ''}{bold ? '👷 ' : ''}{p.name}
    </button>
  );
}

function DayCalendar({ sel, first, onChange }) {
  const today = todayYmd();
  const [month, setMonth] = useState(() => { const d = at(first); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const label = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const cells = [];
  const lead = (month.getDay() + 6) % 7;            // Monday first
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = new Date(month); d.getMonth() === month.getMonth(); d.setDate(d.getDate() + 1)) cells.push(ymd(d));
  while (cells.length % 7) cells.push(null);
  const full = sel.size >= MAX_DAYS;
  const flip = d => { const n = new Set(sel); if (n.has(d)) n.delete(d); else if (!full) n.add(d); onChange(n); };
  const shift = k => setMonth(m => new Date(m.getFullYear(), m.getMonth() + k, 1));
  // Every weekday still to come in the shown month, up to the limit.
  const weekdaysHere = () => { const n = new Set(sel); for (const c of cells) { if (n.size >= MAX_DAYS) break; if (c && c >= today && ![0, 6].includes(at(c).getDay())) n.add(c); } onChange(n); };
  const nav = { width: 34, height: 34, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 17, cursor: 'pointer', color: '#1e3a5f' };
  const pill = { padding: '4px 10px', borderRadius: 999, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
  return (
    <div>
      <label style={lbl}>Days</label>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, maxWidth: 440 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <button type="button" onClick={() => shift(-1)} aria-label="Previous month" style={nav}>‹</button>
          <div style={{ flex: 1, textAlign: 'center', fontSize: 14.5, fontWeight: 800, color: '#111' }}>{label}</div>
          <button type="button" onClick={() => shift(1)} aria-label="Next month" style={nav}>›</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(w => <div key={w} style={{ textAlign: 'center', fontSize: 10.5, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase' }}>{w}</div>)}
          {cells.map((c, i) => {
            if (!c) return <div key={'x' + i} />;
            const on = sel.has(c), past = c < today, wknd = [0, 6].includes(at(c).getDay());
            const can = !past && (on || !full);
            return (
              <button key={c} type="button" disabled={!can} onClick={() => flip(c)} title={past ? 'Already gone by' : full && !on ? MAX_DAYS + ' days is the most in one go' : ''}
                style={{ height: 38, borderRadius: 8, border: `1.5px solid ${on ? '#1e3a5f' : c === today ? '#93c5fd' : '#eef0f2'}`, background: on ? '#1e3a5f' : wknd ? '#f9fafb' : '#fff',
                  color: on ? '#fff' : past ? '#d1d5db' : wknd ? '#9ca3af' : '#111', fontSize: 13.5, fontWeight: on ? 800 : 600, cursor: can ? 'pointer' : 'default' }}>
                {at(c).getDate()}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: sel.size ? '#1e3a5f' : '#9ca3af', flex: 1 }}>{sel.size ? `${sel.size} day${sel.size === 1 ? '' : 's'} picked` : 'Tap the days to work'}{full ? ' (the most in one go)' : ''}</span>
          <button type="button" onClick={weekdaysHere} style={pill}>All weekdays this month</button>
          {sel.size > 0 && <button type="button" onClick={() => onChange(new Set())} style={pill}>Clear</button>}
        </div>
      </div>
    </div>
  );
}
